import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ROLES = [
  { key: 'manager', label: 'Project Manager', code: 'manager' },
  { key: 'task_manager', label: 'Task Manager', code: 'task_manager' },
  { key: 'client_manager', label: 'Client Manager', code: 'client_manager' },
  { key: 'finance', label: 'Finance', code: 'finance' },
  { key: 'hr', label: 'HR', code: 'hr' },
  { key: 'sales', label: 'Sales', code: 'sales' },
  { key: 'marketing', label: 'Marketing', code: 'marketing' },
  { key: 'isms_manager', label: 'ISMS Manager', code: 'isms_manager' },
  { key: 'user', label: 'User (default)', code: 'user' },
] as const;

type RoleKey = (typeof ROLES)[number]['key'];

interface Row {
  area: string;
  source: string;
  access: Partial<Record<RoleKey, string>>;
  note?: string;
}

const ROWS: Row[] = [
  { area: 'App settings', source: 'routes/appSettings.ts:32', access: {}, note: 'admin only' },
  { area: 'App access registry', source: 'routes/appAccess.ts:56', access: { isms_manager: '✓' } },
  { area: 'Audit trail', source: 'routes/auditTrail.ts:16', access: { manager: '✓', isms_manager: '✓' } },
  { area: 'Capacity planning', source: 'routes/capacity.ts:36', access: { manager: '✓', hr: '✓' } },
  { area: 'Employee extra costs', source: 'routes/employeeExtraCosts.ts:29', access: { finance: '✓' } },
  { area: 'Employee extra cost types', source: 'routes/employeeExtraCostTypes.ts:21', access: { finance: '✓' } },
  { area: 'Extra costs (company)', source: 'routes/extraCosts.ts:21+', access: { finance: '✓' } },
  { area: 'Employee types — read', source: 'routes/employeeTypes.ts:23', access: { finance: '✓', hr: '✓' }, note: 'mutate = admin only' },
  { area: 'Event types', source: 'routes/eventTypes.ts:19', access: { hr: '✓' } },
  { area: 'Expense sheets — manager workflow', source: 'routes/expenseSheets.ts:185+', access: { manager: '✓' }, note: 'mark-paid = finance' },
  { area: 'External user admin', source: 'routes/externalUserAdmin.ts:53', access: {}, note: 'admin only' },
  { area: 'Freelancer/employee invoices (read)', source: 'routes/freelancerInvoices.ts:12', access: { finance: '✓' } },
  { area: 'Invoices (CRUD)', source: 'routes/invoices.ts:31+', access: { finance: '✓' } },
  { area: 'Project join requests', source: 'routes/joinRequests.ts:42', access: { manager: '✓' } },
  { area: 'Leads — CRUD', source: 'routes/leads.ts:54+', access: { sales: '✓' } },
  { area: 'Leads — read', source: 'routes/leads.ts:65,70,100', access: { sales: '✓', marketing: '✓' }, note: 'requireSalesOrMarketing' },
  { area: 'Periods (admin)', source: 'routes/periods.ts:23+', access: {}, note: 'admin only' },
  { area: 'Project labels — admin', source: 'routes/projects.ts:87+', access: {}, note: 'admin only' },
  { area: 'Project admin / templates / roles', source: 'routes/projectAdminRoutes.ts:30, projectTemplateAdminRoutes.ts:35, projectRoles.ts:35+', access: {}, note: 'admin only' },
  { area: 'Projects — general mutate', source: 'routes/projects.ts:314+', access: { manager: '✓' } },
  { area: 'Project state (per-project)', source: 'routes/projectStateRoutes.ts:56', access: { manager: 'PM' }, note: 'requireAdminOrProjectPM' },
  { area: 'Project tasks config (per-project)', source: 'routes/projectTasksRoutes.ts:45', access: { manager: 'PM' }, note: 'requireAdminOrProjectPM' },
  { area: 'Milestones (per-project)', source: 'routes/milestoneRoutes.ts:50,67', access: { manager: 'M/PM', user: 'M' }, note: 'list = AdminOrProjectMemberByProject; mutate = AdminOrProjectPM' },
  { area: 'Tasks — general management', source: 'routes/tasks.ts:185+', access: { manager: '✓', task_manager: '✓' } },
  { area: 'Task mutations (move/assign/visibility)', source: 'routes/taskMutationRoutes.ts:86,169,195', access: { manager: 'M/PM', user: 'M' }, note: 'requireAdminOrProjectMember' },
  { area: 'Time entries — manager ops', source: 'routes/timeEntry.ts:347,374,383,408,429', access: { manager: '✓' } },
  { area: 'Time entries — admin-only ops', source: 'routes/timeEntry.ts:459+', access: {}, note: 'admin only' },
  { area: 'Time-off — manager/HR workflow', source: 'routes/timeOff.ts:277+', access: { manager: '✓', hr: '✓' } },
  { area: 'Time-off — admin-only ops', source: 'routes/timeOff.ts:57+', access: {}, note: 'admin only' },
  { area: 'Reports — payroll', source: 'routes/reports.ts:61', access: { finance: '✓', hr: '✓' } },
  { area: 'Reports — finance (rates, invoicing, costs)', source: 'routes/reports.ts:37,85,109,133,157,181,205', access: { finance: '✓' } },
  { area: 'Reports — projects (summary/detailed)', source: 'routes/reports.ts:231,263', access: { manager: '✓ scoped' }, note: 'manager scoping in service layer' },
  { area: 'User rates', source: 'routes/userRates.ts:39+', access: { finance: '✓' } },
  { area: 'User role tags', source: 'routes/userRoleTags.ts:17', access: {}, note: 'admin only' },
  { area: 'Users — list / detail / assignments (read)', source: 'routes/users.ts:79,91,208', access: { hr: '✓', isms_manager: '✓' } },
  { area: 'Users — CRUD / restore', source: 'routes/users.ts:106,145,177,236', access: {}, note: 'admin only' },
  { area: 'Web analytics', source: 'routes/webAnalytics.ts:24+', access: { marketing: '✓' } },
  { area: 'Workflow admin (N8N onboarding/offboarding)', source: 'routes/workflowAdmin.ts:31+', access: {}, note: 'admin only' },
];

function Cell({ value }: { value?: string }) {
  if (!value) return <span className="text-gray-300">·</span>;
  if (value === '✓') return <span className="text-green-600 font-semibold">✓</span>;
  return <span className="text-amber-600 font-medium" title="Conditional access">{value}</span>;
}

export default function PermissionsPage() {
  const navigate = useNavigate();
  const [selectedRoles, setSelectedRoles] = useState<Set<RoleKey>>(new Set());
  const [includeAdminOnly, setIncludeAdminOnly] = useState(false);

  const toggleRole = (key: RoleKey) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearFilter = () => {
    setSelectedRoles(new Set());
    setIncludeAdminOnly(false);
  };

  const filteredRows = useMemo(() => {
    if (selectedRoles.size === 0) return ROWS;
    return ROWS.filter((row) => {
      const hasMatchingRole = Array.from(selectedRoles).some((key) => Boolean(row.access[key]));
      const isAdminOnly = Object.keys(row.access).length === 0;
      if (hasMatchingRole) return true;
      if (includeAdminOnly && isAdminOnly) return true;
      return false;
    });
  }, [selectedRoles, includeAdminOnly]);

  const filterActive = selectedRoles.size > 0 || includeAdminOnly;

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Permissions Overview</h1>
        <p className="text-sm text-gray-500 mt-1">
          Role-to-feature matrix per Phase 27 D-04 (Petřina need-to-know matrix).
          Static reference — derived from <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">backend/src/api/middleware/rbac.ts</code> and route definitions in <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">backend/src/api/routes/</code>.
          Update this page manually when permissions change.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Roles</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
          <li><span className="font-medium">admin</span> — superset; passes every gate (D-12 OR-union). Not shown as a column below; assume always ✓.</li>
          {ROLES.map((r) => (
            <li key={r.key}><span className="font-medium">{r.code}</span> — {r.label}</li>
          ))}
        </ul>
        <p className="text-xs text-gray-500 pt-2">
          Legacy values <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">system_administrator</code> and <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">robot</code> are kept defensively for service accounts (no gated routes).
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Cell legend</h2>
        <ul className="text-sm text-gray-700 space-y-1">
          <li><span className="text-green-600 font-semibold mr-2">✓</span> role passes the route guard unconditionally</li>
          <li><span className="text-amber-600 font-medium mr-2">PM</span> only when the user is <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">Project.projectManagerId</code> for that project</li>
          <li><span className="text-amber-600 font-medium mr-2">M</span> only when the user has a non-deleted <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">ProjectAssignment</code> on that project</li>
          <li><span className="text-amber-600 font-medium mr-2">✓ scoped</span> passes the gate, but the service layer further filters records (e.g. manager sees only own-managed projects)</li>
          <li><span className="text-gray-300 mr-2">·</span> role does NOT pass</li>
        </ul>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Special gates</h2>
        <ul className="text-sm text-gray-700 space-y-1">
          <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">requireAdminOrProjectPM</code> — admin OR <code className="text-xs">Project.projectManagerId === user.id</code> (param: <code className="text-xs">:projectId</code>)</li>
          <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">requireAdminOrProjectMember</code> — admin OR PM OR project assignment (param: <code className="text-xs">:id</code> resolves task → project)</li>
          <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">requireAdminOrProjectMemberByProject</code> — same as above but uses <code className="text-xs">:projectId</code> directly</li>
          <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">requireSalesOrMarketing</code> — read-only access for marketing on Leads</li>
          <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">requireAdminFinanceManagerOrPM</code> — admin + finance + manager + PM on both project reports (MKS-330); admin and finance are unscoped, manager/PM are scoped to their own projects in <code className="text-xs">projectReportService</code> / <code className="text-xs">detailedProjectReportService</code></li>
        </ul>
        <p className="text-xs text-gray-500 pt-2">
          Impersonation: per Phase 27 D-13, role checks always derive from the real admin (<code className="text-xs">request.realUser</code>), not the impersonated target. Admin impersonating a non-admin keeps admin powers; admin impersonating an HR user does NOT gain HR powers through the target.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filter by role</h2>
          <div className="text-xs text-gray-500">
            {filterActive ? (
              <span>
                Showing {filteredRows.length} of {ROWS.length} rows
                <button onClick={clearFilter} className="ml-3 text-primary-600 hover:underline">
                  Clear
                </button>
              </span>
            ) : (
              <span>Showing all {ROWS.length} rows</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {ROLES.map((r) => {
            const active = selectedRoles.has(r.key);
            return (
              <button
                key={r.key}
                onClick={() => toggleRole(r.key)}
                className={
                  'px-3 py-1 rounded-full text-xs font-medium border transition-colors ' +
                  (active
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50')
                }
              >
                {r.code}
              </button>
            );
          })}
        </div>
        {selectedRoles.size > 0 && (
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeAdminOnly}
              onChange={(e) => setIncludeAdminOnly(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Also show admin-only rows
          </label>
        )}
      </div>

      <div className="overflow-auto rounded-lg border border-gray-200 bg-white" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <table className="divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[280px] min-w-[280px]">
                Functional area
              </th>
              {ROLES.map((r) => {
                const active = selectedRoles.has(r.key);
                return (
                  <th
                    key={r.key}
                    className={
                      'px-3 py-3 text-center text-xs font-medium uppercase whitespace-nowrap ' +
                      (active ? 'bg-primary-50 text-primary-700' : 'text-gray-500')
                    }
                  >
                    {r.code}
                  </th>
                );
              })}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[260px]">
                Special gate / note
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={ROLES.length + 2} className="px-4 py-8 text-center text-sm text-gray-500">
                  No rows match the selected filter. Try toggling roles or enable &ldquo;Also show admin-only rows&rdquo;.
                </td>
              </tr>
            )}
            {filteredRows.map((row, idx) => (
              <tr key={row.area} className={idx % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'}>
                <td className={`sticky left-0 z-10 px-4 py-2 w-[280px] min-w-[280px] ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="font-medium text-gray-900">{row.area}</div>
                  <div className="text-xs text-gray-400 font-mono">{row.source}</div>
                </td>
                {ROLES.map((r) => {
                  const active = selectedRoles.has(r.key);
                  return (
                    <td
                      key={r.key}
                      className={'px-3 py-2 text-center ' + (active ? 'bg-primary-50/50' : '')}
                    >
                      <Cell value={row.access[r.key]} />
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-xs text-gray-600">{row.note ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Source of truth: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">backend/src/api/middleware/rbac.ts</code> + route file <code className="text-xs">preHandler</code> definitions.
      </p>
    </div>
  );
}
